/**
 * `/interests` — the sales desk: every enquiry, wherever it came from.
 *
 * Rows arrive two ways — the form on wendhq.com, and staff typing in a lead
 * that came by phone or in person — and land in one list, because the owner
 * asked for "all the sales tickets at one place". The source is shown on every
 * row so nobody has to guess which kind they are looking at.
 *
 * It opens on **Open** (new + in progress), not All: a closed enquiry is
 * history, and a desk that opens on history buries the one row that arrived
 * this morning. The counts on the filter buttons are across every interest, so
 * "New 3" is true whatever the search box holds.
 *
 * Like `/tenants`, it is a directory and not a work surface. Status changes and
 * notes live on the detail screen behind the name, where there is room for the
 * timeline that says who did what and when.
 *
 * THE FILTER IS `ScopeChips` (slice 66). It was already a row of pressed/not
 * pressed buttons in a named group; it is now the product's own version of
 * that control, so it looks and behaves like the scope chips in the tenant
 * app. One behaviour is kept deliberately: before the first answer arrives the
 * chips carry **no** count at all, rather than the component's "not measured"
 * mark — the counts have not been asked for yet, which is a loading state, not
 * a measurement that failed. Status is `@wend/ui`'s Badge; the local `Pill` it
 * replaces was the same classes under another name.
 */
import { Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { DEFAULT_PAGE_SIZE, INTEREST_FILTER_VALUES, type InterestFilter } from '@wend/shared';
import { Badge, Button, Card, Input, KICKER_CLASS, ScopeChips, Spinner, buttonVariants, cn } from '@wend/ui';

import { AdminShell } from '../components/AdminShell';
import { ErrorState } from '../components/ErrorState';
import { formatDateTime, formatRange, formatRelative } from '../lib/format';
import {
  INTEREST_FILTER_LABEL,
  INTEREST_SOURCE_LABEL,
  INTEREST_STATUS_LABEL,
  INTEREST_STATUS_TONE,
} from '../lib/interest-status';
import { listInterests } from '../lib/interests-api';
import { useAsyncData } from '../lib/use-async-data';

/** Typing pauses this long before the list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

const HEAD_CELL = cn('px-4 py-3 text-left text-muted', KICKER_CLASS);

export function InterestsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<InterestFilter>('open');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const { status, data, error, refreshing, reload } = useAsyncData(
    (signal) =>
      listInterests(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          status: filter,
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [page, search, filter],
  );

  const pagination = data?.pagination ?? null;
  const items = data?.items ?? [];
  const counts = data?.counts ?? null;
  const isFirstLoad = status === 'loading' && data === null;

  const countFor = (value: InterestFilter): number | null => {
    if (counts === null) return null;
    switch (value) {
      case 'open':
        return counts.new + counts.in_progress;
      case 'all':
        return counts.new + counts.in_progress + counts.closed;
      default:
        return counts[value];
    }
  };

  return (
    <AdminShell
      title="Interests"
      actions={
        <Link to="/interests/new" className={buttonVariants({ size: 'sm' })}>
          <Plus aria-hidden="true" className="size-4" />
          New interest
        </Link>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <ScopeChips
            label="Show interests"
            selection="single"
            value={filter}
            items={INTEREST_FILTER_VALUES.map((value) => {
              const total = countFor(value);
              // No count until the first answer: see the header.
              return total === null
                ? { value, label: INTEREST_FILTER_LABEL[value] }
                : { value, label: INTEREST_FILTER_LABEL[value], count: total };
            })}
            onValueChange={(value) => {
              setFilter(value as InterestFilter);
              setPage(1);
            }}
          />

          <div className="relative min-w-0 flex-1 sm:max-w-xs">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              type="search"
              className="pl-9"
              placeholder="Search name, email, company, phone"
              aria-label="Search interests"
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
              }}
            />
          </div>

          {refreshing && <Spinner size="sm" label="Updating the list" />}

          {pagination !== null && (
            <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              {formatRange(pagination.page, pagination.pageSize, pagination.total)}
            </p>
          )}
        </div>

        <Card>
          {isFirstLoad && (
            <div className="flex items-center justify-center gap-3 px-6 py-16 text-muted">
              <Spinner size="lg" label="Loading interests" />
            </div>
          )}

          {!isFirstLoad && status === 'error' && error !== null && (
            <ErrorState error={error} title="Could not load interests" onRetry={reload} />
          )}

          {!isFirstLoad && status !== 'error' && items.length === 0 && (
            <div className="flex flex-col items-start gap-3 px-6 py-16">
              <p className="text-[0.95rem] text-muted">
                {search !== ''
                  ? `No interest matches “${search}”.`
                  : filter === 'open'
                    ? 'Nothing open. Enquiries from the website form appear here.'
                    : filter === 'all'
                      ? 'No interests yet.'
                      : `No interests are ${INTEREST_FILTER_LABEL[filter].toLowerCase()}.`}
              </p>
              {search === '' && (
                <Link to="/interests/new" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
                  Add one by hand
                </Link>
              )}
            </div>
          )}

          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[0.9rem]">
                <thead>
                  <tr className="border-b border-line">
                    <th scope="col" className={HEAD_CELL}>
                      Who
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      What they said
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      Status
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      Received
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      Last activity
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((interest) => (
                    <tr key={interest.id} className="border-b border-line last:border-b-0">
                      <td className="px-4 py-3.5 align-top">
                        {/* The name is the link — `TenantsPage` says why not the row. */}
                        <Link
                          to={`/interests/${interest.id}`}
                          className="font-medium leading-snug text-text underline-offset-4 hover:underline"
                        >
                          {interest.name}
                        </Link>
                        <p className="text-[0.8rem] text-text/85">{interest.organisation}</p>
                        <p className="font-mono text-[0.72rem] tracking-[0.04em] text-muted">
                          {interest.email ?? interest.phone ?? '—'}
                        </p>
                      </td>

                      <td className="max-w-[28rem] px-4 py-3.5 align-top">
                        <p className="line-clamp-2 text-[0.85rem] leading-snug text-muted">
                          {interest.messageExcerpt === '' ? (
                            <span className="italic">No message</span>
                          ) : (
                            interest.messageExcerpt
                          )}
                        </p>
                        <p className={cn('mt-1 text-muted', KICKER_CLASS)}>
                          {INTEREST_SOURCE_LABEL[interest.source]}
                        </p>
                      </td>

                      <td className="px-4 py-3.5 align-top">
                        <Badge tone={INTEREST_STATUS_TONE[interest.status]}>
                          {INTEREST_STATUS_LABEL[interest.status]}
                        </Badge>
                      </td>

                      <td className="px-4 py-3.5 align-top">
                        <p className="whitespace-nowrap leading-snug">{formatDateTime(interest.createdAt)}</p>
                        <p className="whitespace-nowrap text-[0.75rem] text-muted">
                          {formatRelative(interest.createdAt)}
                        </p>
                      </td>

                      <td className="px-4 py-3.5 align-top">
                        <p className="whitespace-nowrap leading-snug">
                          {formatRelative(interest.lastActivityAt)}
                        </p>
                        <p className="whitespace-nowrap text-[0.75rem] text-muted">
                          {interest.noteCount === 0
                            ? 'No notes yet'
                            : `${String(interest.noteCount)} ${interest.noteCount === 1 ? 'entry' : 'entries'}`}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {pagination !== null && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => {
                setPage((current) => Math.max(1, current - 1));
              }}
            >
              Previous
            </Button>

            <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              Page {pagination.page} of {pagination.totalPages}
            </p>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => {
                setPage((current) => current + 1);
              }}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
